import { useMemo } from 'react';
import { useAtom } from 'jotai';
import tw from 'twin.macro';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';

import { BaseModal, IconButton, SkeletonEmployerCardLoading, Title } from '@restworld/ui-ds';

import { isEmployerCompanyListModalOpenAtom } from '../employer-companies.atoms';
import { CompanyItem } from '../employer-company-list';
import useEmployerCompanyListQuery from '../hooks/use-employer-company-list-query';
import { useEmployerStripePaymentMethods } from '../hooks/use-employer-stripe-payment-methods';
import { ModalHeaderWrapper, ModalCardsWrapper } from '../employer-companies.styled';

const LOADING_SKELETON_COUNT = 3;

function EmployerCompanyListModal({ limit = 100 }: { limit?: number }) {
  const { t } = useTranslation('payment-and-invoices');
  const [isEmployerCompanyListModalOpen, setIsEmployerCompanyListModalOpen] = useAtom(
    isEmployerCompanyListModalOpenAtom
  );
  const { companyList, query } = useEmployerCompanyListQuery({
    params: {
      context: 'employer-company-list-modal',
      limit,
    },
    getAllCompanies: true,
    enabled: isEmployerCompanyListModalOpen,
  });
  const stripePaymentMethods = useEmployerStripePaymentMethods();

  const headerEl = useMemo(
    () => (
      <ModalHeaderWrapper>
        <Title textColor="neutral-700" fontWeight="semibold" twStyle={tw`text-lg`}>
          {t('your_companies')}
        </Title>
        <IconButton ariaLabel={t('close')} Icon={XMarkIcon} onClick={() => setIsEmployerCompanyListModalOpen(false)} />
      </ModalHeaderWrapper>
    ),
    [setIsEmployerCompanyListModalOpen, t]
  );

  return (
    <BaseModal
      onClose={() => setIsEmployerCompanyListModalOpen(false)}
      open={isEmployerCompanyListModalOpen}
      noPadding
      showCloseIcon={false}
      modalStyles={[tw`max-w-[1200px] max-h-[95%] w-[95%] xl:w-[1200px]`]}
      title={headerEl}
    >
      {query.isLoading ? (
        <ModalCardsWrapper>
          {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, index) => (
            <SkeletonEmployerCardLoading key={index} />
          ))}
        </ModalCardsWrapper>
      ) : (
        <ModalCardsWrapper>
          {companyList?.map((company) => {
            return <CompanyItem key={company.id} company={company} stripePaymentMethods={stripePaymentMethods} />;
          })}
        </ModalCardsWrapper>
      )}
    </BaseModal>
  );
}
export default EmployerCompanyListModal;
